import { PointCloudAnalysisLabel } from "@/store/point-cloud-analysis-tool-slice";
import { MouseEvent, RefObject, useCallback, useMemo, type JSX } from "react";
import { MOUSE, Plane, Vector3 } from "three";
import { MeasureLabel } from "../measurements/measure-label";

type AnalysisLableProps = {
  /** Analysis label object */
  label: PointCloudAnalysisLabel;

  /** Reference plane to compute distance value */
  referencePlane: Plane;

  /** The parent that the label should have in the html DOM */
  parentRef: RefObject<HTMLElement>;

  /**
   * Optional scale factor for the labels.
   * For example, a labelsScale of 0.2 will render labels at 20% of their default size.
   */
  labelsScale?: number;

  /** Callback to open the label context menu */
  openContextMenu(labelId: string, position: Vector3): void;
};

/**
 * @returns Analysis label displays a deviation and located and the label position in 3D space.
 */
export function AnalysisLabel({
  label,
  labelsScale,
  referencePlane,
  parentRef,
  openContextMenu,
}: AnalysisLableProps): JSX.Element | null {
  const position = useMemo(
    () => new Vector3().fromArray(label.position),
    [label.position],
  );
  const distance = useMemo(
    () => referencePlane.distanceToPoint(position),
    [position, referencePlane],
  );

  const onPointerDown = useCallback((ev: MouseEvent) => {
    if (ev.button !== MOUSE.RIGHT) return;
    // Need to stop propagation to prevent the scene context menu popup
    ev.stopPropagation();
  }, []);

  const onContextMenu = useCallback(
    (ev: MouseEvent) => {
      ev.preventDefault();
      ev.stopPropagation();
      openContextMenu(label.id, position);
    },
    [label.id, openContextMenu, position],
  );

  return (
    <MeasureLabel
      index={0}
      visible
      position={position}
      parentRef={parentRef}
      distance={distance}
      active
      transparent
      onPointerDown={onPointerDown}
      onContextMenu={onContextMenu}
      pointerEvents="auto"
      scale={labelsScale}
      useSmallUnits
    />
  );
}
